import { useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { $api, invalidateTypedQueries } from '../typedClient'

/**
 * Document audit history for one tenant. The endpoint is gated on
 * gravitate_access, which declares X-Tenant-Name as Literal["Gravitate"], so
 * the header is sent explicitly rather than taken from the selected tenant.
 */
export function useTenantHistory(tenantId: string | null | undefined) {
  return $api.useQuery(
    'post',
    '/meta/tenant/history',
    {
      params: {
        query: { tenant_id: tenantId ?? '' },
        header: { 'X-Tenant-Name': 'Gravitate' },
      },
    },
    {
      enabled: !!tenantId,
      // The drawer is opened on demand and must show the current state, not
      // whatever the 5-minute global staleTime is still holding.
      staleTime: 0,
      refetchOnMount: 'always',
    },
  )
}

/**
 * Rolls a tenant back to the state captured by one audit entry. The backend
 * audits the pre-restore document first, so a restore is itself undoable.
 */
export function useRestoreTenant() {
  const queryClient = useQueryClient()

  return $api.useMutation('post', '/meta/tenant/restore', {
    onSuccess: (data) => {
      invalidateTypedQueries(queryClient, 'post', '/meta/tenant/history')
      invalidateTypedQueries(queryClient, 'post', '/meta/tenant/all')
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      notification.success({
        message: 'Tenant Restored',
        description: `"${data.name}" has been restored to the selected version.`,
      })
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to Restore Tenant',
        description:
          error.detail?.map((d) => d.msg).join(', ') ||
          'An error occurred while restoring the tenant.',
      })
    },
  })
}
